import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { Question, QuestionResponse } from '../../../../interfaces/questions.interface';
import { FormQuestion, FormQuestionOption } from '../../../../interfaces/form-question.interface';
import { Subscription } from 'rxjs/internal/Subscription';
import { CstQuestionnaireService } from '../../../../services/cst-questionnaire.service';
import * as uuid from 'uuid';
import { CommonQuestionnaireService } from '../../../../services/common-questionnaire.service';
import { FormGroupExt } from '../../../../../../form-ext/form-group.ext';
import { FormBuilderExt } from '../../../../../../form-ext/form-builder.ext';
import { AbstractControl, UntypedFormControl, Validators } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { CustomerQuestionnaireService } from '../../../../services/customer-questionnaire.service';
import { CifcReview } from '../../../../interfaces/cifc.interface';
import * as _ from 'lodash';

@Component({
    selector: 'app-form',
    templateUrl: './form.component.html',
    styleUrls: ['./form.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false,
})
export class FormComponent implements OnChanges, OnDestroy, OnInit, AfterViewInit {
    @Input() question: Question;
    @Input() questionIndex: number;
    @ViewChild('titleTextarea') titleTextarea: ElementRef;

    public questionPlaceholder: string;
    public descriptionPlaceholder = $localize`Description (optional)`;
    public answerInputPlaceholder = $localize`Type placeholder text here`;
    public answerSelectPlaceholder = $localize`Type dropdown label here`;
    public answerPlaceholder = $localize`Type placeholder here`;
    public questionTitle: string;
    public MIN_ROWS = 2;
    public MIN_OPTIONS = 1;

    public questionBody: FormQuestion;
    public currentAnswer: FormQuestionOption[] | undefined;
    public formGroup: FormGroupExt;
    public questionReview: CifcReview;
    public inputObject: FormQuestionOption = {
        id: uuid.v4(),
        type: 'input',
        label: '',
        answer: '',
        placeholder: '',
    };
    public selectObject: FormQuestionOption = {
        id: uuid.v4(),
        type: 'select',
        label: '',
        options: ['', '', ''],
        answer: 'Select',
        placeholder: '',
    };

    public subscription: Subscription;
    public questionSubscription: Subscription;
    public reviewSubscription: Subscription;
    public questionChangedOnReviewReset: Subscription;
    public showErrorSubscription: Subscription;

    constructor(
        public customerQuestionnaireService: CustomerQuestionnaireService,
        public cstQuestionnaireService: CstQuestionnaireService,
        public commonQuestionnaireService: CommonQuestionnaireService,
        public cdr: ChangeDetectorRef,
        public formBuilder: FormBuilderExt,
    ) {
        this.subscription = this.customerQuestionnaireService.onQuestionMarked().subscribe((question: Question) => {
            this.questionBody.options = this.currentAnswer;
        });
        if (this.commonQuestionnaireService.createMode) {
            this.questionSubscription = this.cstQuestionnaireService.getQuestionObs().subscribe((question) => {
                this.cstQuestionnaireService.buildQuestion(this, question);
            });
            this.showErrorSubscription = this.cstQuestionnaireService.showErrorOnForm.asObservable().subscribe((val) => {
                this.TitleControl?.markAsTouched();
                this.cdr.detectChanges();
            });
        }
    }

    public get TitleControl(): AbstractControl | null {
        return this.formGroup?.group.get('title');
    }

    public get DescriptionControl(): AbstractControl | null {
        return this.formGroup?.group.get('description');
    }

    ngOnInit(): void {
        this.initForm();

        this.reviewSubscription = this.commonQuestionnaireService.reviewAddedObs().subscribe((review: CifcReview) => {
            if (review?.question_id === this.question.id && review.company_id === this.commonQuestionnaireService.reviewCompanyId) {
                this.questionReview = review;
            }
        });
        if (this.commonQuestionnaireService.reviewMode) {
            this.questionChangedOnReviewReset = this.commonQuestionnaireService
                .questionChangedOnReviewResetObs()
                .subscribe((question: Question) => {
                    if (question.id === this.question.id && question.body.company_id === this.commonQuestionnaireService.reviewCompanyId) {
                        this.currentAnswer = question.body.options as FormQuestionOption[];
                    }
                    (this.questionReview as any) = _.cloneDeep(
                        this.commonQuestionnaireService.initialReviewQuestions.find((x) => x.id === this.question.id)?.cifc_review,
                    );
                    if (this.questionReview?.solved) {
                        (this.questionReview as any) = null;
                    }
                });
        }
    }

    ngAfterViewInit(): void {
        if (this.titleTextarea) {
            this.titleTextarea.nativeElement.style.height = this.titleTextarea.nativeElement.scrollHeight + 'px';
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes && changes.question && changes.question.currentValue !== changes.question.previousValue) {
            this.question = changes.question.currentValue;
            this.cstQuestionnaireService.buildQuestion(this, changes.question.currentValue);
            this.initForm();
            this.TitleControl?.setValue(this.question.title);
            this.DescriptionControl?.setValue(this.question.description);

            this.addOptionToCurrentForm();
            this.cstQuestionnaireService.currentQuestionForm = this.formGroup;
            this.cdr.detectChanges();
        }
    }

    ngOnDestroy() {
        this.subscription?.unsubscribe();
        this.questionSubscription?.unsubscribe();
        this.reviewSubscription?.unsubscribe();
        this.questionChangedOnReviewReset?.unsubscribe();
        this.showErrorSubscription?.unsubscribe();
        this.cdr.detach();
    }

    public onQuestionMarked($event: boolean) {
        this.question.answerChanged = !$event;
    }

    public onInputValueChanged($event: string) {
        if ($event !== '' && this.currentAnswer?.filter((x) => x.answer && x.answer !== 'Select').length === this.currentAnswer?.length) {
            this.question.answerChanged = true;
        }
    }

    public onSelectionChanged($event: MatSelectChange) {
        if (this.currentAnswer?.filter((x) => x.answer && x.answer !== 'Select').length === this.currentAnswer?.length) {
            this.question.answerChanged = true;
        }
    }

    public hasCompletedForm() {
        return (
            (this.currentAnswer?.filter((x) => x.answer && x.answer !== 'Select').length === this.currentAnswer?.length &&
                this.question.answerChanged) ||
            (this.question.cifc_review && !this.question.cifc_review.solved) ||
            (this.question as unknown as QuestionResponse).body.deleted
        );
    }

    public initForm() {
        this.formGroup = this.formBuilder.groupExt(
            {
                title: [this.question.title, [Validators.required]],
                description: [this.question.description],
            },
            {},
            {
                title: {
                    required: $localize`Please enter a title for this question`,
                },
            },
        );
        this.addOptionToCurrentForm();
        this.cstQuestionnaireService.currentQuestionForm = this.formGroup;
    }

    public onLabelChange(option: FormQuestionOption, i: number, type: 'input' | 'select') {
        if (this.currentAnswer) {
            this.currentAnswer[i].label = this.getOptionControl(i, type, 'label')?.value;
            this.cstQuestionnaireService.currentQuestionForm = this.formGroup;
        }
    }

    public changeSelectionEvent(event: any) {
        event.source.value = 'Select';
    }

    public onTitleChange() {
        this.question.title = this.TitleControl?.value;
    }

    public onDescriptionChange() {
        this.question.description = this.DescriptionControl?.value;
    }

    public addOptionToCurrentForm() {
        this.currentAnswer?.forEach((option: FormQuestionOption, index: number) => {
            // adding the input/select label controls
            if (this.formGroup && !this.formGroup?.group.contains('option_' + option.type + '_' + index + '_label')) {
                const errorName = 'option_' + option.type + '_' + index + '_label';
                this.formGroup?.group.addControl(
                    'option_' + option.type + '_' + index + '_label',
                    new UntypedFormControl(option.label || '', Validators.required),
                );
                this.formGroup?.addError(errorName, {
                    required: 'You need to write a label for this option',
                });
            } else {
                this.getOptionControl(index, option.type, 'label')?.setValue(option.label);
            }

            // adding options controls to select
            if (option.type === 'select') {
                option.options?.forEach((opt, i) => {
                    if (!this.formGroup?.group.contains('option_' + option.type + '_' + index + '_opt_' + i)) {
                        const errorOptName = 'option_' + option.type + '_' + index + '_opt_' + i;
                        this.formGroup?.group.addControl(
                            'option_' + option.type + '_' + index + '_opt_' + i,
                            new UntypedFormControl(opt || '', Validators.required),
                        );
                        this.formGroup?.addError(errorOptName, {
                            required: 'You need to write a label for this option',
                        });
                    } else {
                        this.getSelectOptionControl(i, index)?.setValue(opt);
                    }
                });
            }
        });
    }

    public onSelectOptionChange(optIndex: number, fieldIndex: number) {
        if (this.currentAnswer && this.currentAnswer[fieldIndex] && this.currentAnswer[fieldIndex].options) {
            // @ts-ignore
            this.currentAnswer[fieldIndex].options[optIndex] = this.getSelectOptionControl(optIndex, fieldIndex)?.value;
            this.cstQuestionnaireService.currentQuestionForm = this.formGroup;
        }
    }

    public getOptionControl(index: number, type: 'input' | 'select', valueType: 'label' | 'placeholder') {
        return this.formGroup?.group.get('option_' + type + '_' + index + '_' + valueType);
    }

    public getSelectOptionControl(index: number, fieldIndex: number) {
        return this.formGroup?.group.get('option_select' + '_' + fieldIndex + '_opt_' + index);
    }

    public areAllOptionsForSelectCompleted() {
        let areAllCompleted = true;
        // @ts-ignore
        this.currentAnswer?.forEach((opt, index) => {
            if (opt.type === 'select') {
                opt.options?.forEach((o, i) => {
                    if (o === '' && this.getSelectOptionControl(i, index)?.touched) {
                        areAllCompleted = false;
                    }
                });
            }
        });

        return areAllCompleted;
    }

    public addNewField(type: 'select' | 'input') {
        this.cstQuestionnaireService.addQuestionField(
            this.currentAnswer,
            this.cstQuestionnaireService.getObjectWithRandomId(type === 'select' ? this.selectObject : this.inputObject),
        );
        this.addOptionToCurrentForm();
    }

    public deleteField(fieldIndex: number) {
        if (this.currentAnswer) {
            if (this.currentAnswer[fieldIndex].type === 'select') {
                this.formGroup.group.removeControl('option_' + this.currentAnswer[fieldIndex].type + '_' + fieldIndex + '_label');
                // @ts-ignore
                this.currentAnswer[fieldIndex].options.forEach((opt, i) => {
                    this.formGroup.group.removeControl('option_select' + '_' + fieldIndex + '_opt_' + i);
                });
            }

            if (this.currentAnswer[fieldIndex].type === 'input') {
                this.formGroup.group.removeControl('option_' + this.currentAnswer[fieldIndex].type + '_' + fieldIndex + '_label');
                this.formGroup.group.removeControl('option_' + this.currentAnswer[fieldIndex].type + '_' + fieldIndex + '_placeholder');
            }
            this.cstQuestionnaireService.currentQuestionForm = this.formGroup;
        }
        this.cstQuestionnaireService.deleteQuestionField(this.currentAnswer, fieldIndex, this.MIN_ROWS);
    }

    public trackByFn(index: any, item: any) {
        return index;
    }

    public addOption($event: Event, fieldIndex: number, field: FormQuestionOption) {
        $event.stopPropagation();

        field.options?.push('');
        field.options?.forEach((op, i) => {
            this.formGroup.group.removeControl('option_select' + '_' + fieldIndex + '_opt_' + i);
        });
        this.addOptionToCurrentForm();
    }

    public deleteOption($event: any, field: FormQuestionOption, fieldIndex: number, index: number) {
        $event.stopImmediatePropagation();
        if (this.validateMinOptions(field)) {
            field.options?.splice(index, 1);
        }
        this.formGroup.group.removeControl('option_select_' + fieldIndex + '_opt_' + index);
        field.options?.forEach((op, i) => {
            this.formGroup.group.removeControl('option_select' + '_' + fieldIndex + '_opt_' + i);
        });
        this.addOptionToCurrentForm();
        this.cstQuestionnaireService.currentQuestionForm = this.formGroup;
    }

    public validateMinOptions(field: FormQuestionOption): boolean {
        return field.options ? field.options.length > this.MIN_OPTIONS : false;
    }
}
